import * as angular from 'angular';

import { IRootScope } from 'IRootScope';
import { ComponentDefinition, ServiceStatus, ResultsStatus, IDateRange, LastProcessedDisplay, LastProcessingResult, Group } from '../../../models';
import { IAuthorizationService, ITaskService, IOnlineComputationService, IJobDefinitionService, IlastProcessingResultsService, IDateNavigationService, IUserSettingsService, IStoreService, IAuthenticationService, IDateHelperFormatService, ITranslatedLoggerService } from '../../../services';
import { isEditMode } from '../../../common';
import { StoreElement } from '../../../stateManagement';

class JobsDefinitionStateController implements angular.IComponentController {
    canEdit: boolean;
    // TODO Remove exposed scope
    exposedScope: { refresh: () => void; };
    format: string;
    groups: { [guid: string]: Group };
    isEditMode: boolean;
    isElapsedDisplayed: boolean = false;
    isError: boolean;
    isLoading: boolean;
    isLoadingLastProcessed: boolean;
    isOffline: boolean;
    lastProcessedDisplay: LastProcessedDisplay[];
    loadingMessage: string;
    titleElapsedDisplay: string;

    private _lastResultsRange: IDateRange;
    private _watcher: () => void;

    static $inject = [
        '$scope',
        '$rootScope',
        '$translate',
        'storeService',
        'authorizationService',
        'dateHelperFormatService',
        'dateNavigationService',
        'jobDefinitionService',
        'lastProcessingResultsService',
        'translatedLoggerService',
        'onlineComputationService',
        'taskService',
        'userSettingsService'
    ];
    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$translate: angular.translate.ITranslateService,
        private _storeService: IStoreService,
        private _authorizationService: IAuthorizationService,
        private _dateHelperFormatService: IDateHelperFormatService,
        private _dateNavigationService: IDateNavigationService,
        private _jobDefinitionService: IJobDefinitionService,
        private _lastProcessingResultsService: IlastProcessingResultsService,
        private _loggerService: ITranslatedLoggerService,
        private _onlineComputationService: IOnlineComputationService,
        private _taskService: ITaskService,
        private _userSettingsService: IUserSettingsService
    ) {
        this.format = this._dateHelperFormatService.getDateFormat();
    }

    $onInit() {
        this.isElapsedDisplayed = this._userSettingsService.getSettingValue('isElapsedDisplayed') === 'true';
        this.isOffline = false;
        this.isError = true;
        this.canEdit = this._authorizationService.canEdit();
        this.isEditMode = this._$rootScope.isEditMode;
        this._$scope.$on(isEditMode, (event, data) => {
            this.isEditMode = data;
        });
        this.refreshStatus();
        this.lastProcessedDisplay = [];
        this.titleElapsedDisplay = this.isElapsedDisplayed
            ? this._$translate.instant('Hide execution time charts')
            : this._$translate.instant('Show execution time charts');
        this._watcher = this._storeService.register(
            store => this.groups = store.groups.dictionary,
            [StoreElement.Groups]
        );
    }

    $onDestroy() {
        if (this._watcher) {
            this._watcher();
        }
    }

    refresh() {
        return this.refreshStatus();
    }

    previous() {
        const endDate = this._lastResultsRange.beginDate;
        const beginDate = this._dateNavigationService.getPreviousAtN(endDate, 100);
        this._lastResultsRange = { beginDate, endDate };
        this.refreshLastProcessed(beginDate, endDate);
    }

    next() {
        const beginDate = this._lastResultsRange.endDate;
        const endDate = this._dateNavigationService.getNextAtN(beginDate, 101); // for open brackets on the right
        this._lastResultsRange = { beginDate, endDate };
        this.refreshLastProcessed(beginDate, endDate);
    }

    private refreshStatus() {
        this.isLoading = true;
        this.isError = false;
        this.isOffline = false;
        this._lastResultsRange = this._dateNavigationService.getLastNPeriod(100);
        const { beginDate, endDate } = this._lastResultsRange;
        return this.refreshLastProcessed(beginDate, endDate)
            .then(() => this._onlineComputationService.status())
            .then(status => {
                this.isOffline = status === ServiceStatus.Offline;
                this.isError = false;
                return status;
            })
            .catch(() => this.isError = true)
            .finally(() => this.isLoading = false);
    }

    private refreshLastProcessed(beginDate: moment.Moment, endDate: moment.Moment): angular.IPromise<LastProcessingResult[]> {
        this.isLoadingLastProcessed = true;
        return this._lastProcessingResultsService
            .getLastProcessingResults(beginDate.toISOString(), endDate.toISOString(), true)
            .then(data => {
                this.lastProcessedDisplay = data.map(d => {
                    return {
                        beginDate: new Date(d.beginDate),
                        endDate: new Date(d.endDate),
                        classed: ResultsStatus[d.status]?.toLocaleLowerCase(),
                        message: `${d.beginDate} - ${d.endDate}: ${ResultsStatus[d.status]}`
                    };
                });
                return data;
            })
            .finally(() => this.isLoadingLastProcessed = false);
    }

    refreshTasks() {
        this._taskService.refresh()
            .then(() => this._$rootScope.$broadcast('tasksChanged'));
    }

    cancelAllTasks() {
        this._taskService.cancelAll().then(() => {
            this.refreshTasks();
        });
    }

    clearCompletedTasks() {
        this._taskService.deleteCompletedtasks()
            .then(() => {
                this._loggerService.logSuccess('Completed tasks successfully deleted');
                this._$rootScope.$broadcast('tasksChanged');
            })
            .catch(() => {
                this._loggerService.logError('Failed to delete completed tasks.');
            });
    }

    clearInactiveJobs() {
        this._jobDefinitionService.clearInactive()
            .then(() => {
                this._loggerService.logSuccess('Inactive jobs successfully deleted');
                this.exposedScope.refresh();
            })
            .catch(() => {
                this._loggerService.logError('Failed to delete inactive jobs.');
            });
    }

    toggleElapsedDisplayed() {
        this.isElapsedDisplayed = !this.isElapsedDisplayed;
        this.titleElapsedDisplay = this.isElapsedDisplayed
            ? this._$translate.instant('Hide execution time charts')
            : this._$translate.instant('Show execution time charts');
        this._userSettingsService.setSettingValue('isElapsedDisplayed', this.isElapsedDisplayed.toString());
    }
}

export const wtJobsDefinitionState: ComponentDefinition = {
    componentId: 'wtJobsDefinitionState',
    controller: JobsDefinitionStateController,
    template: require('./jobsDefinitionState.component.html'),
    controllerAs: 'vm',
    bindings: {
    }
};